import { onMounted, onUnmounted } from "vue";
import { useClientContextStore } from "../../store/eplex/eplexClientContext";
import { useEplexAccountInfoStore } from "../../store/eplex/eplexAccountInfo";

/**
 * Как часто открытая вкладка переспрашивает, кто в ней сидит и что ему положено.
 * Он же — потолок того, насколько сведения могут отстать от бэкенда.
 */
export const SESSION_REVALIDATE_INTERVAL_MS = 10 * 60 * 1000;

/**
 * Сколько вкладка может пролежать скрытой, прежде чем сведения о сессии
 * считаются протухшими. Короткое переключение на соседнюю вкладку запросов не
 * стоит, ночь простоя — стоит.
 */
export const SESSION_STALE_AFTER_HIDDEN_MS = 2 * 60 * 1000;

/**
 * Свежесть сведений о сессии в долгоживущей вкладке (CAB-734).
 *
 * Кабинет держат открытым сутками: «оставила вечером, вернулась утром» — это
 * обычный сценарий, а не край. Всё это время вкладка жила сведениями, взятыми
 * один раз на монтировании макета, — кто вошёл и что ему положено. За ночь
 * меняется и то, и другое: учётку выводят из онбординга, переводят на другую
 * схему тарификации, а общая кука домена `.eplex.ru` и вовсе подставляет под
 * вкладку другую сессию — вход рядом в соседней вкладке или на стенде.
 *
 * Поэтому сведения перечитываются:
 *
 *   по таймеру      — но только если вкладкой с прошлой проверки пользовались;
 *   при возвращении — если вкладка пролежала скрытой дольше допустимого.
 *
 * Признак «пользовались» здесь не украшение. Бэкенд продлевает пару токенов на
 * каждом запросе, поэтому вкладка, пингующая его по таймеру круглосуточно,
 * держала бы сессию живой вечно — брошенный кабинет перестал бы закрываться сам.
 *
 * Дальше работает обычная цепочка: сменилась личность — перечитывается всё, что
 * к ней привязано (`useIdentityChange`); сняли признак участия — гаснет
 * онбординг (`useOnboardingAccess`).
 */
export function useSessionFreshness(): void {
  const clientContext = useClientContextStore();
  const accountInfo = useEplexAccountInfoStore();

  let timer = 0;
  let hiddenSince = 0;
  let hadInteraction = false;
  let inflight: Promise<void> | null = null;

  /** Никогда не отклоняется: сбой перечитывания не должен всплывать наружу. */
  function revalidate(): Promise<void> {
    if (inflight) return inflight;

    hadInteraction = false;
    inflight = (async () => {
      try {
        // Сначала личность, потом её признаки: смена личности гасит признаки
        // сама, и второй запрос уходит уже за признаками нового человека
        await clientContext.load(true);
        await accountInfo.load(true);
      } catch (error) {
        console.error("Session freshness: не удалось перечитать сведения о сессии", error);
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  }

  function noteInteraction(): void {
    hadInteraction = true;
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === "hidden") {
      hiddenSince = Date.now();
      return;
    }

    const hiddenFor = hiddenSince === 0 ? 0 : Date.now() - hiddenSince;
    hiddenSince = 0;

    if (hiddenFor >= SESSION_STALE_AFTER_HIDDEN_MS) revalidate();
  }

  onMounted(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    document.addEventListener("pointerdown", noteInteraction, { passive: true });
    document.addEventListener("keydown", noteInteraction, { passive: true });

    timer = window.setInterval(() => {
      // Скрытую вкладку не будим: браузер всё равно душит её таймеры, а
      // вернётся человек — перечитаем по видимости
      if (document.visibilityState === "hidden") return;
      // Оставленную открытой, но брошенную — тоже: за неё говорит возвращение
      if (!hadInteraction) return;

      revalidate();
    }, SESSION_REVALIDATE_INTERVAL_MS);
  });

  onUnmounted(() => {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    document.removeEventListener("pointerdown", noteInteraction);
    document.removeEventListener("keydown", noteInteraction);
    window.clearInterval(timer);
  });
}
